import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DOW, SOURCE_COLOR, SOURCE_LABEL, fmtTime, Stat } from "./shared";
import { useAtlas } from "@/context/AtlasContext";

const TYPES = [["Person", "person"], ["Team", "team"], ["Client", "client"], ["Workflow", "workflow"], ["Module", "module"], ["AI Agent", "agent"]];

const PERSON_TYPES = new Set(["person", "agent"]);

export default function PulseExplorer() {
  const { has } = useAtlas();
  const canPerson = has("pulse.view_person");
  const types = TYPES.filter(([, v]) => canPerson || !PERSON_TYPES.has(v));
  const [params, setParams] = useSearchParams();
  const type = (params.get("type") && (canPerson || !PERSON_TYPES.has(params.get("type")))) ? params.get("type") : (canPerson ? "person" : "team");
  const id = params.get("id") || "";
  const [idInput, setIdInput] = useState(id);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!id) { setData(null); return; }
    setErr(null);
    api.get(`/pulse/explorer/${type}/${id}`, { params: { days: 30 } }).then((r) => setData(r.data)).catch((e) => { setErr(e?.response?.data?.detail || "Not available"); setData(null); });
  }, [type, id]);

  if (!has("pulse.view")) return <div className="max-w-3xl mx-auto panel p-10 text-center text-sm text-slate-500" data-testid="pulse-forbidden">You don't have access to Pulse in this workspace.</div>;
  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="pulse-explorer">
      <PageHeader title="Pulse Explorer" subtitle="Drill into any object — person, team, client, workflow, module or AI agent — using the same shared event ledger." />
      <div className="panel p-3 flex flex-wrap items-center gap-2">
        <select value={type} onChange={(e) => setParams({ type: e.target.value, id })} className="rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--line)" }} data-testid="explorer-type">
          {types.map(([l, v]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input value={idInput} onChange={(e) => setIdInput(e.target.value)} placeholder="Object ID (e.g. wf-1 for a workflow, or crm for a module)" className="flex-1 min-w-[220px]" data-testid="explorer-id" />
        <button onClick={() => setParams({ type, id: idInput.trim() })} className="px-4 py-2 rounded-full text-white text-sm" style={{ background: "var(--ink)" }} data-testid="explorer-go">Inspect →</button>
      </div>
      {!id && <div className="panel p-8 text-center text-sm text-slate-400">Enter an object ID to open its Pulse profile. Person/agent detail requires the <code>pulse.view_person</code> permission.</div>}
      {err && <div className="panel p-6 text-center text-sm text-amber-700" data-testid="explorer-error">{err}</div>}
      {data && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Activity (30d)" value={data.activity_total} testid="profile-activity" />
          <Stat label="Completed" value={data.outcomes?.completed} accent="#0f766e" testid="profile-completed" />
          <Stat label="Created" value={data.outcomes?.created} testid="profile-created" />
          <Stat label="Sources" value={Object.keys(data.by_source || {}).length} sub={Object.entries(data.by_source || {}).map(([k, n]) => `${SOURCE_LABEL[k] || k} ${n}`).join(" · ")} testid="profile-sources" />
        </div>
        <div className="panel p-4">
          <p className="eyebrow mb-2">Active periods</p>
          <MiniWall periods={data.active_periods} />
        </div>
        <div className="panel p-4" data-testid="profile-recent">
          <p className="eyebrow mb-2">Recent events</p>
          <div className="space-y-1.5">{(data.recent || []).map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-1 border-b last:border-0" style={{ borderColor: "var(--line)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: SOURCE_COLOR[e.source] || SOURCE_COLOR.other }} />
              <span className="flex-1 truncate text-slate-700">{e.summary}</span><span className="text-[11px] text-slate-400">{fmtTime(e.at)}</span>
            </div>))}</div>
        </div>
      </>}
    </div>);
}

function MiniWall({ periods }) {
  const map = {}; (periods || []).forEach((c) => { map[`${c.dow}-${c.hour}`] = c.count; });
  const peak = Math.max(1, ...(periods || []).map((c) => c.count));
  return (<div className="overflow-x-auto"><div className="min-w-[560px]">
    {DOW.map((d, dow) => (<div key={dow} className="flex items-center gap-1 mb-1">
      <div className="w-9 text-[10px] text-slate-500 text-right pr-1">{d}</div>
      {Array.from({ length: 24 }).map((_, h) => { const v = map[`${dow}-${h}`] || 0; return <div key={h} className="flex-1 aspect-square rounded-sm" style={{ background: v ? `rgba(15,118,110,${0.15 + 0.85 * v / peak})` : "var(--line)", minWidth: 12 }} title={`${d} ${h}:00 · ${v}`} />; })}
    </div>))}
  </div></div>);
}
